<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import InputFieldGroup from './InputFieldGroup.vue';
import RequiredLabel from './RequiredLabel.vue';
import ErrorSlot from './ErrorSlot.vue';

const props = defineProps<{
  label: string;
  errorText?: string | null;
  required?: boolean;
  modelValue: number | null;
  placeholder?: string;
  showSteppers?: boolean;
  min?: number;
  step?: number;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

const inputValue = ref<string>(formatCzk(props.modelValue));
const isFocused = ref(false);

function formatCzk(value: number | null): string {
  if (value == null || value <= 0) return '';
  const rounded = Math.round(value);
  const str = String(rounded);
  let buf = '';
  for (let i = 0; i < str.length; i++) {
    if (i > 0 && (str.length - i) % 3 === 0) buf += ' ';
    buf += str[i];
  }
  return buf;
}

let lastSynced = inputValue.value;

watch(
  () => props.modelValue,
  (value) => {
    const formatted = formatCzk(value);
    if (formatted !== lastSynced && formatted !== inputValue.value) {
      inputValue.value = formatted;
      lastSynced = formatted;
    } else if (formatted === inputValue.value) {
      lastSynced = formatted;
    }
  },
);

function onInput(e: Event): void {
  const target = e.target as HTMLInputElement;
  const raw = target.value.replace(/[^0-9]/g, '');
  const formatted = formatCzk(raw === '' ? 0 : Number(raw));
  target.value = formatted;
  lastSynced = formatted;
  inputValue.value = formatted;
  emit('update:modelValue', raw === '' ? 0 : Number(raw));
}

function onFocus(e: FocusEvent): void {
  isFocused.value = true;
  const target = e.target as HTMLInputElement;
  const current = props.modelValue ?? 0;
  if (current > 0) {
    target.value = String(current).replace(/[^0-9]/g, '');
  } else {
    target.value = '';
    inputValue.value = '';
    lastSynced = '';
  }
}

function onBlur(e: FocusEvent): void {
  isFocused.value = false;
  const target = e.target as HTMLInputElement;
  const formatted = formatCzk(props.modelValue);
  target.value = formatted;
  lastSynced = formatted;
  inputValue.value = formatted;
}

function decrement(): void {
  const min = props.min ?? 0;
  const step = props.step ?? 1;
  const next = Math.max(min, (props.modelValue ?? 0) - step);
  emit('update:modelValue', next);
}

function increment(): void {
  const step = props.step ?? 1;
  emit('update:modelValue', (props.modelValue ?? 0) + step);
}

const hasError = computed(() => props.errorText != null);
</script>

<template>
  <div class="block" data-testid="input-money">
    <RequiredLabel
      :text="label"
      :required="required ?? false"
      :has-error="hasError"
    />
    <InputFieldGroup
      :has-error="hasError"
      :stepper="showSteppers ? 'plusminus' : 'none'"
      @increment="increment"
      @decrement="decrement"
    >
      <input
        v-model="inputValue"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        class="flex-1 min-w-0 h-10 px-2.5 bg-transparent outline-none text-body-medium text-text-primary placeholder:text-text-tertiary"
        :placeholder="placeholder"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <span class="px-2.5 text-label-small text-text-tertiary">Kč</span>
    </InputFieldGroup>
    <ErrorSlot :text="errorText ?? null" />
  </div>
</template>
